import { useEffect, useState } from 'react';
import type { TargetingConfig } from '../../types/metaAdDraft';
import type { ReachEstimateResult } from '../../types/metaAdResources';
import { estimateReach } from './api';

// Stage 7A D1 — reach estimate hook.
//
// Meta computes the delivery estimate ASYNCHRONOUSLY: the first call commonly
// returns `{ available: true, estimateReady: false, retryAfterMs }` with
// placeholder zeros — meaning "still computing, poll again in retryAfterMs". We
// poll on that hint until Meta reports ready (estimateReady !== false) or we hit
// the cap, then stop. Each poll loop is keyed by the serialised targeting: a spec
// change abandons the in-flight loop and starts fresh (so a stale poll can't
// overwrite a newer result), and unmount cancels it.
//
// Self-contained setTimeout loop (not react-query refetchInterval): the readiness
// is driven by Meta's own retryAfterMs, the cap must leave isPolling=false (so the
// UI shows "unavailable" rather than spinning forever), and the timing is
// deterministically testable with mock timers.

export const REACH_MAX_POLLS = 10; // hard cap on total requests (1 initial + retries)
export const REACH_DEFAULT_RETRY_MS = 2000; // used when Meta omits retryAfterMs
export const REACH_MAX_RETRY_MS = 10_000; // clamp Meta's hint (never wait absurdly long)

// Pure: the delay (ms) before the next poll given how many requests have already
// fired, Meta's readiness + estimable flags, and its retry hint. `false` = stop
// polling — ready (true) / unknown (null), the cap reached, OR estimable:false (a
// dead-end spec Zernio/Meta can't estimate — Miki 2026-06-15; surface "couldn't
// estimate" rather than spinning). `attempts` is the count of requests ALREADY
// made (so the first poll passes attempts=1).
export function nextReachPollDelay(
  attempts: number,
  estimateReady: boolean | null,
  retryAfterMs: number | null,
  estimable: boolean | null = null,
): number | false {
  if (estimable === false) return false; // dead-end spec — stop, don't keep polling
  if (estimateReady !== false) return false; // ready (true) or unknown (null) → done
  if (attempts < 1 || attempts >= REACH_MAX_POLLS) return false; // cap reached
  const hint =
    typeof retryAfterMs === 'number' && retryAfterMs > 0 ? retryAfterMs : REACH_DEFAULT_RETRY_MS;
  return Math.min(hint, REACH_MAX_RETRY_MS);
}

export type UseReachEstimate = {
  data: ReachEstimateResult | null;
  isLoading: boolean; // first fetch in flight (no data yet)
  isPolling: boolean; // have a "still computing" response + a scheduled retry
  isError: boolean;
};

const IDLE: UseReachEstimate = { data: null, isLoading: false, isPolling: false, isError: false };

export function useReachEstimate(
  accountId: string,
  targeting: TargetingConfig,
  optimizationGoal: string,
  enabled: boolean,
): UseReachEstimate {
  // Serialised spec — the effect re-runs (abandoning any in-flight poll loop)
  // whenever the targeting, account, goal, or enablement changes.
  const specKey = JSON.stringify(targeting);
  const [state, setState] = useState<UseReachEstimate>(IDLE);

  useEffect(() => {
    if (!enabled || accountId.trim().length === 0) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    setState({ data: null, isLoading: true, isPolling: false, isError: false });

    const poll = async (): Promise<void> => {
      attempts += 1;
      let data: ReachEstimateResult;
      try {
        data = await estimateReach(accountId, targeting, optimizationGoal);
      } catch {
        if (!cancelled) setState({ data: null, isLoading: false, isPolling: false, isError: true });
        return;
      }
      if (cancelled) return; // superseded / unmounted — never overwrite a newer loop
      const delay = nextReachPollDelay(
        attempts,
        data.estimateReady ?? null,
        data.retryAfterMs ?? null,
        data.estimable ?? null,
      );
      if (delay === false) {
        // Ready, unknown, or capped out. isPolling=false either way; a capped-out
        // estimateReady:false surfaces as "unavailable" in the panel (not zeros).
        setState({ data, isLoading: false, isPolling: false, isError: false });
        return;
      }
      setState({ data, isLoading: false, isPolling: true, isError: false });
      timer = setTimeout(() => {
        void poll();
      }, delay);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specKey captures `targeting`
  }, [accountId, specKey, optimizationGoal, enabled]);

  return state;
}
